<template>
  <AppModal :visible="visible === true" :title="modalTitle" boxClass="max-w-3xl w-11/12" @close="handleClose">
    <div v-if="visible === true" class="p-4 space-y-4">
      <div v-if="questionText !== ''" class="bg-base-200 rounded-lg p-3">
        <div class="text-sm font-semibold mb-1">{{ t('reports.sourcesModal.questionLabel') }}</div>
        <div class="text-sm whitespace-pre-wrap break-words">{{ questionText }}</div>
      </div>
      <div v-if="isLoading === true" class="flex justify-center items-center py-6">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
      <div v-else-if="errorMessage !== ''" class="text-center text-error font-medium py-6">
        {{ errorMessage }}
      </div>
      <div v-else>
        <div v-if="sources.length === 0" class="text-center text-base-content py-6">
          {{ emptyStateMessage }}
        </div>
        <div v-else class="sources-modern-container" :class="{ 'sources-modern-container-rtl': isRTL === true }" style="max-height: 55vh; overflow-y: auto;">
          <div v-for="(source, index) in sources" :key="index" class="source-modern-card" :class="{ 'source-modern-card-rtl': isRTL === true }">
            <div class="source-modern-header" :class="{ 'source-modern-header-rtl': isRTL === true }">
              <Icon :icon="source.type === 'website' ? 'tabler:world' : 'tabler:file'" class="source-modern-icon" width="20" height="20" />
              <span class="source-modern-name">{{ source.name || formatSourceLabel(source, index) }}</span>
            </div>
            <a v-if="source.type === 'website' && source.url !== undefined && source.url !== null" :href="source.url" target="_blank" rel="noopener noreferrer" class="source-modern-url" :class="{ 'source-modern-url-rtl': isRTL === true }">
              <Icon icon="tabler:external-link" width="14" height="14" />
              <span>{{ source.url }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n';
import { globalState } from '@/globalState';
import AppModal from './AppModal.vue';
import router from '@/router';
import { Icon } from '@iconify/vue/dist/offline';

const props = defineProps({
  visible: { type: Boolean, default: false },
  sessionId: { type: String, default: '' },
  messageId: { type: String, default: '' }
});

const emit = defineEmits(['close']);

const toast = useToast();
const { t, currentLanguage } = useI18n();

const isRTL = computed(() => {
  const lang = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
  return lang === 'he' || lang === 'ar' || (typeof lang === 'string' && (lang.startsWith('he') === true || lang.startsWith('ar') === true));
});

const isLoading = ref(false);
const sources = ref([]);
const questionText = ref('');
const errorMessage = ref('');

const modalTitle = computed(() => t('reports.sourcesModal.title'));
const emptyStateMessage = computed(() => t('reports.sourcesModal.emptyState'));

const resetState = () => {
  isLoading.value = false;
  sources.value = [];
  questionText.value = '';
  errorMessage.value = '';
};

const formatSourceLabel = (source, index) => {
  if (source.type === 'website') {
    return `${t('reports.sourcesModal.websiteSourceLabel')} ${index + 1}`;
  }
  if (source.type === 'file') {
    return `${t('reports.sourcesModal.fileSourceLabel')} ${index + 1}`;
  }
  return `${t('reports.sourcesModal.genericSourceLabel')} ${index + 1}`;
};

const getUniqueSources = (sources) => {
  const seen = new Set();
  const unique = [];
  for (const source of sources) {
    const key = source.url !== undefined && source.url !== null && source.url !== ''
      ? source.url.trim().toLowerCase()
      : (source.name !== undefined && source.name !== null && source.name !== ''
          ? source.name.trim().toLowerCase()
          : null);
    if (key !== null && seen.has(key) === false) {
      seen.add(key);
      unique.push(source);
    } else if (key === null) {
      unique.push(source);
    }
  }
  return unique;
};

const loadSources = async () => {
  if (props.visible !== true) {
    return;
  }
  const sessionId = typeof props.sessionId === 'string' ? props.sessionId.trim() : '';
  const messageId = typeof props.messageId === 'string' ? props.messageId.trim() : '';
  if (sessionId.length === 0 || messageId.length === 0) {
    errorMessage.value = t('reports.sourcesModal.invalidIdentifiers');
    sources.value = [];
    questionText.value = '';
    return;
  }
  isLoading.value = true;
  sources.value = [];
  questionText.value = '';
  errorMessage.value = '';
  try {
    const endpoint = `${globalState.apiEndpoint}/api/reports/conversation/${encodeURIComponent(sessionId)}/message/${encodeURIComponent(messageId)}/sources`;
    const response = await axios.get(endpoint, { withCredentials: true });
    const data = response.data;
    if (data !== undefined && data !== null) {
      const src = Array.isArray(data.sources) === true ? data.sources : [];
      sources.value = getUniqueSources(src);
      if (typeof data.question === 'string') {
        questionText.value = data.question;
      }
      if (src.length === 0) {
        errorMessage.value = '';
      }
    }
  } catch (error) {
    if (error.response !== undefined && (error.response.status === 401 || error.response.status === 403)) {
      emit('close');
      router.push('/login');
      return;
    }
    if (error.response !== undefined && error.response.status === 404) {
      errorMessage.value = t('reports.sourcesModal.notFound');
    } else {
      errorMessage.value = t('reports.sourcesModal.loadFailed');
      toast.error(t('reports.sourcesModal.loadFailed'));
    }
    console.error('Error loading knowledge sources:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

watch(
  () => props.visible,
  (newValue, oldValue) => {
    if (newValue === true) {
      loadSources();
    } else if (oldValue === true) {
      resetState();
    }
  }
);

watch(
  () => props.messageId,
  (newValue, oldValue) => {
    if (props.visible === true && newValue !== oldValue) {
      loadSources();
    }
  }
);
</script>

<style scoped>
.sources-modern-container {
  display: flex;
  flex-direction: column;
  direction: ltr;
}
.sources-modern-container-rtl {
  direction: rtl;
}
.source-modern-card {
  background-color: var(--fallback-b1,rgba(255,255,255,0.6));
  border: 1px solid var(--fallback-b2,rgba(148,163,184,0.25));
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
  margin-top: 0.75rem;
  transition: all 0.2s ease;
  direction: ltr;
}
.source-modern-card:first-of-type {
  margin-top: 0;
}
.source-modern-card:hover {
  background-color: var(--fallback-b1,rgba(255,255,255,0.8));
  border-color: var(--fallback-b2,rgba(148,163,184,0.4));
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}
.source-modern-card-rtl {
  direction: rtl;
}
.source-modern-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  direction: ltr;
}
.source-modern-header-rtl {
  flex-direction: row-reverse;
  direction: rtl;
}
.source-modern-icon {
  color: var(--fallback-p,#3b82f6);
  margin-right: 0.625rem;
  flex-shrink: 0;
}
.source-modern-header-rtl .source-modern-icon {
  margin-right: 0;
  margin-left: 0.625rem;
}
.source-modern-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--fallback-bc,rgba(30,41,59,0.9));
  word-break: break-word;
  line-height: 1.4;
}
.source-modern-url {
  display: inline-flex;
  align-items: center;
  color: var(--fallback-p,#2563eb);
  text-decoration: none;
  font-size: 0.875rem;
  word-break: break-all;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  background-color: var(--fallback-b1,rgba(59,130,246,0.1));
  transition: all 0.2s ease;
  direction: ltr;
  margin-top: 0.25rem;
}
.source-modern-url:hover {
  background-color: var(--fallback-b1,rgba(59,130,246,0.15));
  text-decoration: underline;
}
.source-modern-url svg {
  margin-right: 0.375rem;
  flex-shrink: 0;
}
.source-modern-url-rtl {
  direction: rtl;
  flex-direction: row-reverse;
}
.source-modern-url-rtl svg {
  margin-right: 0;
  margin-left: 0.375rem;
}
</style>

